import { Link } from "react-router-dom";
import { Instagram, Heart, MessageCircle, ArrowUpRight } from "lucide-react";
import { FadeUp, SectionHead } from "@/components/Reveal";

const HANDLE = "https://www.instagram.com/urwandenims/";

/* Sample feed styled after @urwandenims — each post is shoppable and linked to its piece. */
const POSTS = [
    { productId: "p-selvedge-raw", img: "/img/urwan/2J7A0775.jpg", name: "Vintage Denim Pant – Selvedge Raw", price: 6299, likes: "2,431" },
    { productId: "s-ice-blue", img: "/img/urwan/2J7A0742.jpg", name: "Vintage Denim Shirt – Ice Blue", price: 2969, likes: "1,876" },
    { productId: "p-dark-night", img: "/img/urwan/2J7A0534.jpg", name: "Earthy Denim Pant – Dark Night", price: 5099, likes: "3,102" },
    { productId: "s-macho-blue", img: "/img/urwan/DSC00722.jpg", name: "Vintage Denim Shirt – Macho Blue", price: 2699, likes: "1,544" },
    { productId: "s-stripes-green", img: "/img/urwan/BD0A8494.jpg", name: "Earthy Stripes – White and Green", price: 1499, likes: "987" },
    { productId: "p-indigo-night", img: "/img/urwan/Urwan_03_15.jpg", name: "Chino Denim Pant – Indigo Night", price: 4599, likes: "1,203" },
];

const inr = (n) => `₹${n.toLocaleString("en-IN")}`;

export default function InstagramFeedStrip() {
    return (
        <section className="border-t border-white/10 bg-coal/40 py-24 md:py-32" data-testid="insta-strip">
            <div className="mx-auto flex w-full max-w-[1400px] flex-wrap items-end justify-between gap-6 px-5 md:px-8 lg:px-10">
                <SectionHead
                    eyebrow="@urwandenims"
                    title="Follow The Feed"
                    sub="Shop the looks straight from the URWAN Instagram — tap a post, own the piece."
                />
                <FadeUp>
                    <a
                        href={HANDLE}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="insta-follow"
                        className="group inline-flex items-center gap-3 border border-gold/50 px-6 py-3.5 text-[11px] font-mono uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-black"
                    >
                        <Instagram size={16} className="transition-transform duration-300 group-hover:rotate-6" />
                        Follow @urwandenims
                    </a>
                </FadeUp>
            </div>

            <FadeUp delay={0.1}>
                <div className="mt-12 flex snap-x gap-4 overflow-x-auto px-5 pb-4 md:gap-5 md:px-8 lg:px-10">
                    {POSTS.map((p) => (
                        <Link
                            key={p.productId}
                            to={`/product/${p.productId}`}
                            data-testid={`insta-post-${p.productId}`}
                            className="group relative block aspect-square w-60 shrink-0 snap-start overflow-hidden bg-elev md:w-72"
                        >
                            <img
                                src={p.img}
                                alt={p.name}
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 transition-opacity duration-300 group-hover:opacity-100" />

                            {/* instagram chrome */}
                            <span className="absolute left-3 top-3 flex items-center gap-2 rounded-full border border-white/20 bg-black/45 py-1 pl-1 pr-3 backdrop-blur">
                                <span className="grid h-6 w-6 place-items-center rounded-full border border-gold/60 text-gold">
                                    <Instagram size={11} />
                                </span>
                                <span className="text-[10px] font-mono tracking-[0.15em] text-ink">urwandenims</span>
                            </span>
                            <span className="absolute right-3 top-3 flex items-center gap-3 text-white/90 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                                <span className="flex items-center gap-1 text-xs">
                                    <Heart size={14} fill="currentColor" className="text-gold" /> {p.likes}
                                </span>
                                <MessageCircle size={14} />
                            </span>

                            {/* shoppable chip — always visible on touch, hover-revealed on desktop */}
                            <span
                                data-testid={`insta-shop-${p.productId}`}
                                className="absolute inset-x-3 bottom-3 flex translate-y-1 items-center justify-between gap-2 opacity-100 transition-all duration-300 md:translate-y-2 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100"
                            >
                                <span className="max-w-[70%] truncate text-[11px] font-medium text-ink">{p.name}</span>
                                <span className="flex shrink-0 items-center gap-1.5 bg-gold px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-black">
                                    Shop {inr(p.price)}
                                    <ArrowUpRight size={11} />
                                </span>
                            </span>
                        </Link>
                    ))}
                </div>
            </FadeUp>

            <p className="mt-6 px-5 text-center text-[10px] font-mono uppercase tracking-[0.3em] text-mute md:px-8 lg:px-10">
                Swipe the feed — every look is one tap from the wardrobe
            </p>
        </section>
    );
}
